import { useState, useCallback, useEffect } from 'react';
import { useCompany } from '../contexts/CompanyContext';
import { payrollConfigAPI } from '../api/payrollConfig';

export function usePayrollConfig() {
  const { currentCompany } = useCompany();

  const [parametros, setParametros] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [tssRates, setTssRates] = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);
  const [deductionTypes, setDeductionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    setError(null);
    try {
      const [p, c, t, i, d] = await Promise.all([
        payrollConfigAPI.getParametros(),
        payrollConfigAPI.getConceptos(),
        payrollConfigAPI.getTSSRates(),
        payrollConfigAPI.getIncomeTypes(),
        payrollConfigAPI.getDeductionTypes()
      ]);
      setParametros(p);
      setConceptos(c);
      setTssRates(t);
      setIncomeTypes(i);
      setDeductionTypes(d);
    } catch (err) {
      console.error('Error cargando configuración de nómina:', err);
      setError(err.response?.data?.error || 'Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ─── Parámetros ─────────────────────────────────────────────
  const saveParametros = async (nuevosParametros) => {
    const saved = await payrollConfigAPI.saveParametros(nuevosParametros);
    setParametros(saved);
    return saved;
  };

  // ─── Conceptos ──────────────────────────────────────────────
  const createConcepto = async (data) => {
    const created = await payrollConfigAPI.createConcepto(data);
    setConceptos(prev => [...prev, created]);
    return created;
  };

  const updateConcepto = async (id, data) => {
    const updated = await payrollConfigAPI.updateConcepto(id, data);
    setConceptos(prev => prev.map(c => c.id === id ? updated : c));
    return updated;
  };

  const deleteConcepto = async (id) => {
    await payrollConfigAPI.deleteConcepto(id);
    setConceptos(prev => prev.filter(c => c.id !== id));
  };

  // ─── Tasas TSS ──────────────────────────────────────────────
  const saveTSSRates = async (tasas) => {
    const saved = await payrollConfigAPI.saveTSSRates(tasas);
    setTssRates(saved);
    return saved;
  };

  // ─── Tipos de Ingresos ──────────────────────────────────────
  const createIncomeType = async (data) => {
    const created = await payrollConfigAPI.createIncomeType(data);
    setIncomeTypes(prev => [...prev, created]);
    return created;
  };

  const updateIncomeType = async (id, data) => {
    const updated = await payrollConfigAPI.updateIncomeType(id, data);
    setIncomeTypes(prev => prev.map(t => t.id === id ? updated : t));
    return updated;
  };

  const deleteIncomeType = async (id) => {
    await payrollConfigAPI.deleteIncomeType(id);
    setIncomeTypes(prev => prev.filter(t => t.id !== id));
  };

  // ─── Tipos de Deducciones ───────────────────────────────────
  const createDeductionType = async (data) => {
    const created = await payrollConfigAPI.createDeductionType(data);
    setDeductionTypes(prev => [...prev, created]);
    return created;
  };

  const updateDeductionType = async (id, data) => {
    const updated = await payrollConfigAPI.updateDeductionType(id, data);
    setDeductionTypes(prev => prev.map(t => t.id === id ? updated : t));
    return updated;
  };

  const deleteDeductionType = async (id) => {
    await payrollConfigAPI.deleteDeductionType(id);
    setDeductionTypes(prev => prev.filter(t => t.id !== id));
  };

  return {
    parametros,
    conceptos,
    tssRates,
    incomeTypes,
    deductionTypes,
    loading,
    error,
    loadAll,
    saveParametros,
    createConcepto,
    updateConcepto,
    deleteConcepto,
    saveTSSRates,
    createIncomeType,
    updateIncomeType,
    deleteIncomeType,
    createDeductionType,
    updateDeductionType,
    deleteDeductionType
  };
}

export default usePayrollConfig;
